/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';

import Button, { ButtonStyleType } from '@/components/common/Button/Button';

import DatePickerTag4 from '@/components/common/DatePicker/DatePickerTag4';
import Dropdown from '@/components/common/Dropdown/Dropdown';
import Toggle from '@/components/common/Toggle/Toggle';
import { ContractTCsFormValues } from '../ContractTCs.container';
import {
  SpecialContractTypeEnum,
  NoticePeriodTypeEnum
} from '@/graphql/contracts/_generated_/contracts';
import styles from './contract-tcs-form.module.scss';

const MAX_ADDITIONAL_INFO_LENGTH = 2000;

const validationSchema = Yup.object().shape({
  specialContractType: Yup
    .string()
    .nullable()
    .oneOf(
      [...Object.values(SpecialContractTypeEnum), null, ''],
      'Invalid special contract type'
    ),
  fixedTermAdditionalInfo: Yup
    .string()
    .max(MAX_ADDITIONAL_INFO_LENGTH, `Must be at most ${MAX_ADDITIONAL_INFO_LENGTH} characters`),
  breakClauseAdditionalInfo: Yup
    .string()
    .max(MAX_ADDITIONAL_INFO_LENGTH, `Must be at most ${MAX_ADDITIONAL_INFO_LENGTH} characters`),
  noticePeriodType: Yup
    .string()
    .nullable()
    .oneOf(
      [...Object.values(NoticePeriodTypeEnum), null, ''],
      'Invalid notice period type'
    ),
  cancellationAdditionalInfo: Yup
    .string()
    .max(MAX_ADDITIONAL_INFO_LENGTH, `Must be at most ${MAX_ADDITIONAL_INFO_LENGTH} characters`),
});

interface SpecialContractTypeOption {
  label: string;
  value: SpecialContractTypeEnum;
  description?: string;
}

interface NoticePeriodTypeOption {
  label: string;
  value: NoticePeriodTypeEnum;
}

const specialContractTypeOptions: SpecialContractTypeOption[] = [
  { label: 'None', value: SpecialContractTypeEnum.None },
  {
    label: 'G-Cloud',
    value: SpecialContractTypeEnum.GCloud,
    description: 'An agreement between a public sector organisation and a supplier for cloud-based computing services, procured through the UK government\'s G-Cloud framework, which facilitates access to pre-approved suppliers via the Digital Marketplace.'
  },
  {
    label: 'Agreed Extension',
    value: SpecialContractTypeEnum.AgreedExtension,
    description: 'An agreement that does not automatically renew but can extend for a certain period if agreed between the customer and the Account Manager.'
  },
  {
    label: 'Access Fixed',
    value: SpecialContractTypeEnum.AccessFixed,
    description: 'An agreement that does not auto-renew and does not include any information about extension periods.'
  }
];

const noticePeriodTypeOptions: NoticePeriodTypeOption[] = [
  { label: 'Standard', value: NoticePeriodTypeEnum.Standard },
  { label: 'Other', value: NoticePeriodTypeEnum.Other }
];

interface IContractTCsFormProps {
  initialValues: ContractTCsFormValues;
  isLoading: boolean;
  isLoadingUpdate: boolean;
  isEditable: boolean;
  onSubmit: (values: ContractTCsFormValues) => void;
  customerName: string;
  customerCode: string;
  contractNumber: string;
  startDate: string;
  endDate: string;
  totalValue: string;
  fixedTermUpdatedBy: string | null;
  fixedTermUpdatedDate: string | null;
  breakClauseUpdatedBy: string | null;
  breakClauseUpdatedDate: string | null;
  cancellationUpdatedBy: string | null;
  cancellationUpdatedDate: string | null;
}

interface IFormFieldsProps {
  isDisabled: boolean;
  isEditable: boolean;
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
  initialValues: ContractTCsFormValues;
  customerName: string;
  customerCode: string;
  contractNumber: string;
  startDate: string;
  endDate: string;
  totalValue: string;
  fixedTermUpdatedBy: string | null;
  fixedTermUpdatedDate: string | null;
  breakClauseUpdatedBy: string | null;
  breakClauseUpdatedDate: string | null;
  cancellationUpdatedBy: string | null;
  cancellationUpdatedDate: string | null;
}

const formatUpdatedDate = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const getUpdatedByText = (
  updatedBy: string | null,
  updatedDate: string | null
): string => {
  if (!updatedBy && !updatedDate) return 'No changes recorded';
  const formattedDate = formatUpdatedDate(updatedDate);
  const user = updatedBy ?? 'Unknown';
  return `Last updated by ${user} on ${formattedDate}`;
};

function FormFields({
  isDisabled,
  isEditable,
  isDirty,
  setIsDirty,
  initialValues,
  customerName,
  customerCode,
  contractNumber,
  startDate,
  endDate,
  totalValue,
  fixedTermUpdatedBy,
  fixedTermUpdatedDate,
  breakClauseUpdatedBy,
  breakClauseUpdatedDate,
  cancellationUpdatedBy,
  cancellationUpdatedDate
}: IFormFieldsProps) {
  const { values, setFieldValue, submitForm, resetForm, errors, touched, isValid } =
    useFormikContext<ContractTCsFormValues>();

  useEffect(() => {
    const hasChanges =
      values.specialContractType !== initialValues.specialContractType ||
      values.fixedTermReviewDate?.getTime() !==
      initialValues.fixedTermReviewDate?.getTime() ||
      values.fixedTermAdditionalInfo !==
      initialValues.fixedTermAdditionalInfo ||
      values.breakClauseEnabled !== initialValues.breakClauseEnabled ||
      values.breakClauseReviewDate?.getTime() !==
      initialValues.breakClauseReviewDate?.getTime() ||
      values.breakClauseAdditionalInfo !==
      initialValues.breakClauseAdditionalInfo ||
      values.noticePeriodType !== initialValues.noticePeriodType ||
      values.cancellationAdditionalInfo !==
      initialValues.cancellationAdditionalInfo;

    setIsDirty(hasChanges);
  }, [values, initialValues, setIsDirty]);

  const handleBreakClauseToggle = () => {
    const newEnabled = !values.breakClauseEnabled;
    setFieldValue('breakClauseEnabled', newEnabled);
  };

  return (
    <Form>
      {/* Contract Summary Header */}
      <div className={styles.contractSummaryBar}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Contracted to</span>
          <span className={styles.summaryValue}>
            [{customerCode}] - {customerName}
          </span>
        </div>
        <div className={styles.summarySeparator} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Contract number</span>
          <span className={styles.summaryValue}>{contractNumber}</span>
        </div>
        <div className={styles.summarySeparator} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Start date</span>
          <span className={styles.summaryValue}>
            {formatUpdatedDate(startDate)}
          </span>
        </div>
        <div className={styles.summarySeparator} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>End date</span>
          <span className={styles.summaryValue}>
            {formatUpdatedDate(endDate)}
          </span>
        </div>
        <div className={styles.summarySeparator} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Total value</span>
          <span className={styles.summaryValue}>{totalValue}</span>
        </div>
      </div>

      {/* Fixed Term Details Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Fixed term details</h3>
          {isEditable && (
            <span className={styles.editableBadge}>
              Editable by Admin / Full Access
            </span>
          )}
        </div>

        <div className={styles.fieldsRow}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Special contract type
            </label>
            <Dropdown<SpecialContractTypeOption>
              optionsData={specialContractTypeOptions}
              getOptionLabel={(opt) => opt.label}
              getFormatOptionLabel={(opt) => (
                <span title={opt.description ?? ''}>{opt.label}</span>
              )}
              getOptionValue={(opt) => opt.value}
              selectedOption={
                specialContractTypeOptions.find(
                  (opt) => opt.value === values.specialContractType
                ) ?? null
              }
              handleChange={(opt) => {
                setFieldValue('specialContractType', opt?.value ?? initialValues.specialContractType);
              }}
              disabled={isDisabled}
              isClearable={true}
              name="specialContractType"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Fixed term review date
            </label>
            <DatePickerTag4
              value={values.fixedTermReviewDate}
              onChange={(event) => {
                setFieldValue(
                  'fixedTermReviewDate', event.value ?? null
                );
              }}
              disabled={isDisabled}
              clearButton={true}
              name="fixedTermReviewDate"
            />
          </div>
        </div>

        <div className={styles.fullWidthField}>
          <label className={styles.fieldLabel}>
            Additional information
          </label>
          <div className={styles.textareaWrapper}>
            <textarea
              className={`${styles.textarea} ${errors.fixedTermAdditionalInfo
                  && touched.fixedTermAdditionalInfo
                ? styles.textareaError : ''
              }`}
              value={values.fixedTermAdditionalInfo}
              onChange={(e) => {
                const val = e.target.value.slice(
                  0, MAX_ADDITIONAL_INFO_LENGTH
                );
                setFieldValue('fixedTermAdditionalInfo', val);
              }}
              disabled={isDisabled}
              maxLength={MAX_ADDITIONAL_INFO_LENGTH}
              placeholder="Free text — max 2000 characters"
              rows={6}
            />
            <span className={styles.charCount}>
              {values.fixedTermAdditionalInfo.length.toLocaleString()}
              /{MAX_ADDITIONAL_INFO_LENGTH.toLocaleString()}
            </span>
            {errors.fixedTermAdditionalInfo && touched.fixedTermAdditionalInfo && (
              <span className={styles.fieldError}>{errors.fixedTermAdditionalInfo}</span>
            )}
          </div>
        </div>

        <p className={styles.updatedText}>
          {getUpdatedByText(
            fixedTermUpdatedBy, fixedTermUpdatedDate
          )}
        </p>
      </div>

      {/* Break Clause Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitleWithToggle}>
            <h3 className={styles.sectionTitle}>Break clause</h3>
            <div className={styles.toggleWrapper}>
              <Toggle
                isEnable={values.breakClauseEnabled}
                id="breakClauseToggle"
                onChange={handleBreakClauseToggle}
                disabled={isDisabled}
              />
              <span className={styles.toggleLabel}>
                {values.breakClauseEnabled ? 'On' : 'Off'}
              </span>
            </div>
          </div>
          {isEditable && (
            <span className={styles.editableBadge}>
              Editable by Admin / Full Access
            </span>
          )}
        </div>

        <div className={styles.fieldsRow}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Break clause review date
            </label>
            <DatePickerTag4
              value={values.breakClauseReviewDate}
              onChange={(event) => {
                setFieldValue(
                  'breakClauseReviewDate', event.value ?? null
                );
              }}
              disabled={isDisabled || !values.breakClauseEnabled}
              clearButton={true}
              name="breakClauseReviewDate"
            />
          </div>
        </div>

        <div className={styles.fullWidthField}>
          <label className={styles.fieldLabel}>
            Additional information
          </label>
          <div className={styles.textareaWrapper}>
            <textarea
              className={`${styles.textarea} ${errors.breakClauseAdditionalInfo
                  && touched.breakClauseAdditionalInfo
                ? styles.textareaError : ''
              }`}
              value={values.breakClauseAdditionalInfo}
              onChange={(e) => {
                const val = e.target.value.slice(
                  0, MAX_ADDITIONAL_INFO_LENGTH
                );
                setFieldValue('breakClauseAdditionalInfo', val);
              }}
              disabled={isDisabled || !values.breakClauseEnabled}
              maxLength={MAX_ADDITIONAL_INFO_LENGTH}
              placeholder="Free text — max 2000 characters"
              rows={6}
            />
            <span className={styles.charCount}>
              {values.breakClauseAdditionalInfo.length.toLocaleString()}
              /{MAX_ADDITIONAL_INFO_LENGTH.toLocaleString()}
            </span>
            {errors.breakClauseAdditionalInfo && touched.breakClauseAdditionalInfo && (
              <span className={styles.fieldError}>{errors.breakClauseAdditionalInfo}</span>
            )}
          </div>
        </div>

        <p className={styles.updatedText}>
          {getUpdatedByText(
            breakClauseUpdatedBy, breakClauseUpdatedDate
          )}
        </p>
      </div>

      {/* Cancellation Terms Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Cancellation terms</h3>
          {isEditable && (
            <span className={styles.editableBadge}>
              Editable by Admin / Full Access
            </span>
          )}
        </div>

        <div className={styles.fieldsRow}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Notice period type
            </label>
            <Dropdown<NoticePeriodTypeOption>
              optionsData={noticePeriodTypeOptions}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
              selectedOption={
                noticePeriodTypeOptions.find(
                  (opt) => opt.value === values.noticePeriodType
                ) ?? null
              }
              handleChange={(opt) => {
                setFieldValue('noticePeriodType', opt?.value ?? initialValues.noticePeriodType);
              }}
              disabled={isDisabled}
              isClearable={true}
              name="noticePeriodType"
            />
          </div>
        </div>

        <div className={styles.fullWidthField}>
          <label className={styles.fieldLabel}>
            Additional information
          </label>
          <div className={styles.textareaWrapper}>
            <textarea
              className={`${styles.textarea} ${errors.cancellationAdditionalInfo
                  && touched.cancellationAdditionalInfo
                ? styles.textareaError : ''
              }`}
              value={values.cancellationAdditionalInfo}
              onChange={(e) => {
                const val = e.target.value.slice(
                  0, MAX_ADDITIONAL_INFO_LENGTH
                );
                setFieldValue('cancellationAdditionalInfo', val);
              }}
              disabled={isDisabled}
              maxLength={MAX_ADDITIONAL_INFO_LENGTH}
              placeholder="Free text — max 2000 characters"
              rows={6}
            />
            <span className={styles.charCount}>
              {values.cancellationAdditionalInfo.length.toLocaleString()}
              /{MAX_ADDITIONAL_INFO_LENGTH.toLocaleString()}
            </span>
            {errors.cancellationAdditionalInfo && touched.cancellationAdditionalInfo && (
              <span className={styles.fieldError}>{errors.cancellationAdditionalInfo}</span>
            )}
          </div>
        </div>

        <p className={styles.updatedText}>
          {getUpdatedByText(
            cancellationUpdatedBy, cancellationUpdatedDate
          )}
        </p>
      </div>

      {/* Action Buttons */}
      <div className={styles.submitRow}>
        <Button
          type="submit"
          buttonStyleType={ButtonStyleType.PRIMARY}
          onClick={submitForm}
          disabled={!isDirty || isDisabled || !isValid}
        >
          Save
        </Button>
        <Button
          type="button"
          buttonStyleType={ButtonStyleType.SECONDARY}
          onClick={() => resetForm()}
          disabled={!isDirty || isDisabled}
        >
          Cancel
        </Button>
      </div>
    </Form>
  );
}

export default function ContractTCsForm({
  initialValues,
  isLoading,
  isLoadingUpdate,
  isEditable,
  onSubmit,
  customerName,
  customerCode,
  contractNumber,
  startDate,
  endDate,
  totalValue,
  fixedTermUpdatedBy,
  fixedTermUpdatedDate,
  breakClauseUpdatedBy,
  breakClauseUpdatedDate,
  cancellationUpdatedBy,
  cancellationUpdatedDate
}: IContractTCsFormProps) {
  const [isDirty, setIsDirty] = useState(false);

  const isDisabled = !isEditable || isLoading || isLoadingUpdate;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={(values) => {
        onSubmit(values);
        setIsDirty(false);
      }}
    >
      <FormFields
        isDisabled={isDisabled}
        isEditable={isEditable}
        isDirty={isDirty}
        setIsDirty={setIsDirty}
        initialValues={initialValues}
        customerName={customerName}
        customerCode={customerCode}
        contractNumber={contractNumber}
        startDate={startDate}
        endDate={endDate}
        totalValue={totalValue}
        fixedTermUpdatedBy={fixedTermUpdatedBy}
        fixedTermUpdatedDate={fixedTermUpdatedDate}
        breakClauseUpdatedBy={breakClauseUpdatedBy}
        breakClauseUpdatedDate={breakClauseUpdatedDate}
        cancellationUpdatedBy={cancellationUpdatedBy}
        cancellationUpdatedDate={cancellationUpdatedDate}
      />
    </Formik>
  );
}
